/**
 * Fixed-capacity object pool (FS-02 §2.4, §3.2). Allocates `capacity` up front and never again.
 * Release is swap-remove, so iteration order is NOT stable — systems needing deterministic order
 * sort by an explicit key.
 */
export interface Poolable {
  poolReset(): void;
  poolIndex: number;
}

export interface Pool<T extends Poolable> {
  readonly name: string;
  readonly capacity: number;
  readonly live: number;
  readonly peak: number;
  readonly items: readonly T[];
  acquire(): T | null;
  release(item: T): void;
  releaseAll(): void;
}

class ArrayPool<T extends Poolable> implements Pool<T> {
  readonly items: T[];
  private _live = 0;
  private _peak = 0;

  constructor(
    readonly name: string,
    readonly capacity: number,
    factory: (index: number) => T,
  ) {
    this.items = new Array(capacity);
    for (let i = 0; i < capacity; i++) {
      const item = factory(i);
      item.poolIndex = i;
      this.items[i] = item;
    }
  }

  get live(): number {
    return this._live;
  }
  get peak(): number {
    return this._peak;
  }

  acquire(): T | null {
    if (this._live >= this.capacity) return null;
    const item = this.items[this._live]!;
    this._live++;
    if (this._live > this._peak) this._peak = this._live;
    return item;
  }

  release(item: T): void {
    const i = item.poolIndex;
    const last = this._live - 1;
    if (i < 0 || i > last) return; // already released / not live
    item.poolReset();
    if (i !== last) {
      const moved = this.items[last]!;
      this.items[i] = moved;
      this.items[last] = item;
      moved.poolIndex = i;
      item.poolIndex = last;
    }
    this._live--;
  }

  releaseAll(): void {
    for (let i = 0; i < this._live; i++) this.items[i]!.poolReset();
    this._live = 0;
  }
}

export function createPool<T extends Poolable>(
  name: string,
  capacity: number,
  factory: (index: number) => T,
): Pool<T> {
  return new ArrayPool(name, capacity, factory);
}
